import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import type { AlertRule, RuleInput } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t, tOr } from "../i18n.ts";
import { formatNumber } from "../format.ts";
import { depthFirst, siteTree } from "../context.ts";
import {
  conditionText,
  newRule,
  ruleToInput,
  subjectText,
  userName,
} from "../events.ts";
import { Icon, type IconName } from "../icons.tsx";
import { Chart } from "./Chart.tsx";
import { MEDIA_IDS } from "./Media.tsx";
import { unitLabel } from "./PointCard.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import { useAction } from "./ui.tsx";

type Type = "thr" | "nodata" | "anom" | "int" | "cmd";
const TYPES: readonly { key: Type; icon: IconName; ready: boolean }[] = [
  { key: "thr", icon: "gauge", ready: true },
  { key: "nodata", icon: "clock", ready: true },
  { key: "anom", icon: "chart", ready: false },
  { key: "int", icon: "plug", ready: false },
  { key: "cmd", icon: "toggle", ready: false },
];
const STEPS = [
  "subject",
  "condition",
  "recipients",
  "escalation",
  "summary",
] as const;
const DURATIONS = [0, 15, 30, 60, 120] as const;
const NO_DATA = [15, 30, 60, 180] as const;
const ESCALATIONS = [0, 10, 30, 60, 240] as const;
const CHANNELS = ["app", "mail", "sms", "push"] as const;

/**
 * The alert rule wizard (prototype "Nowa reguła alarmu"): kind and subject, condition with a preview on the last
 * week, recipients, escalation, summary with a test mode. Kinds the platform cannot evaluate yet are shown greyed.
 */
export function RuleWizard(props: {
  rule?: AlertRule;
  initial?: RuleInput;
  onClose: () => void;
  onSaved: (rule: AlertRule) => void;
}) {
  const [step, setStep] = createSignal(0);
  const [form, setForm] = createSignal<RuleInput>(
    props.rule ? ruleToInput(props.rule) : (props.initial ?? newRule()),
  );
  const set = (patch: Partial<RuleInput>) => setForm({ ...form(), ...patch });
  const [type, setType] = createSignal<Type>(
    form().kind === "noData" ? "nodata" : "thr",
  );
  const [scopeAll, setScopeAll] = createSignal(
    form().pointId === null && form().kind === "noData",
  );
  const [problem, setProblem] = createSignal<string>();
  const [run, error, busy] = useAction();
  const [users] = createResource(() => api.events.users());

  const points = () =>
    (siteTree()?.points ?? [])
      .filter((p) => p.binding)
      .sort((a, b) => a.name.localeCompare(b.name));
  const nodes = () => depthFirst(siteTree()?.nodes ?? []);
  // Quantities the chosen point's meter reports, to watch one of them in every phase instead of the point's value.
  const [quantities] = createResource(
    () =>
      siteTree()?.points.find((p) => p.id === form().pointId)?.binding
        ?.deviceId,
    async (deviceId) => {
      const latest = await api.devices.latest(deviceId);
      return [
        ...new Set(
          latest
            .filter(
              (v) => v.number !== null && !v.attribute.startsWith("energy."),
            )
            .map((v) => v.attribute),
        ),
      ];
    },
  );
  const voltageBand = () =>
    set({
      parameter: "voltage",
      kind: "outside",
      threshold: 207,
      threshold2: 253,
      minutes: 0,
      name: form().name || t("events.wizard.voltageName"),
    });
  const pointName = (id: string) =>
    siteTree()?.points.find((p) => p.id === id)?.name ?? id;
  const nodeName = (id: string) =>
    siteTree()?.nodes.find((n) => n.id === id)?.name ?? id;
  const num = (s: string) =>
    s.trim() === "" ? null : Number(s.replace(",", "."));

  // Switching the kind keeps what fits it.
  createEffect(
    on(
      type,
      (k) => {
        if (k === "nodata") {
          set({
            kind: "noData",
            threshold: null,
            threshold2: null,
            hysteresis: null,
            minutes: Math.max(form().minutes, 15),
          });
        } else if (k === "thr" && form().kind === "noData") {
          set({
            kind: "above",
            pointId: form().pointId ?? points()[0]?.id ?? null,
            nodeId: null,
            medium: null,
          });
        }
      },
      { defer: true },
    ),
  );

  const [preview] = createResource(
    () =>
      step() === 1 &&
      type() === "thr" &&
      form().pointId &&
      form().threshold !== null &&
      !Number.isNaN(form().threshold)
        ? JSON.stringify(form())
        : undefined,
    () => api.events.previewRule(form()),
  );
  const unit = () => unitLabel(preview()?.unit ?? "");

  const check = (): string | null => {
    const f = form();
    switch (STEPS[step()]) {
      case "subject":
        if (!f.name.trim()) return t("events.wizard.needName");
        if (type() === "thr" && !f.pointId) return t("events.wizard.needPoint");
        if (type() === "nodata" && (scopeAll() ? !f.nodeId : !f.pointId))
          return t("events.wizard.needSubject");
        return null;
      case "condition":
        if (
          type() === "thr" &&
          (f.threshold === null || Number.isNaN(f.threshold))
        )
          return t("events.wizard.needThreshold");
        if (
          f.kind === "outside" &&
          (f.threshold2 === null ||
            Number.isNaN(f.threshold2) ||
            f.threshold2 <= f.threshold!)
        )
          return t("events.wizard.needRange");
        return null;
      case "escalation":
        return f.escalateAfter && !f.escalateTo
          ? t("events.wizard.needEscalateTo")
          : null;
      default:
        return null;
    }
  };
  const next = () => {
    const p = check();
    setProblem(p ?? undefined);
    if (p) {
      return;
    }
    if (step() < STEPS.length - 1) {
      setStep(step() + 1);
      return;
    }
    void run(async () => {
      const f = form();
      const input: RuleInput =
        scopeAll() && f.kind === "noData"
          ? { ...f, pointId: null }
          : { ...f, nodeId: null, medium: null };
      const saved = props.rule
        ? await api.events.updateRule(props.rule.id, input)
        : await api.events.createRule(input);
      props.onSaved(saved);
      props.onClose();
    });
  };

  const onKey = (e: KeyboardEvent) => e.key === "Escape" && props.onClose();
  onMount(() => document.addEventListener("keydown", onKey));
  onCleanup(() => document.removeEventListener("keydown", onKey));

  const hours = createMemo(() =>
    (preview()?.runs ?? []).reduce(
      (h, r) =>
        h + (new Date(r.to).getTime() - new Date(r.from).getTime()) / 3_600_000,
      0,
    ),
  );

  return (
    <div
      class="ovl"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && props.onClose()}
    >
      <div
        class="dlg"
        role="dialog"
        aria-modal="true"
        aria-label={t("events.wizard.title")}
        style={{ width: "min(820px, 100%)" }}
      >
        <div class="dlg-h">
          <div>
            <div class="eyebrow">
              {t("events.wizard.eyebrow")} ·{" "}
              {t("events.wizard.stepOf", {
                step: step() + 1,
                total: STEPS.length,
              })}
            </div>
            <h2>
              {props.rule
                ? t("events.wizard.edit", { name: props.rule.name })
                : t("events.wizard.title")}
            </h2>
          </div>
          <button
            type="button"
            class="btn ghost"
            style={{ "margin-left": "auto" }}
            aria-label={t("common.close")}
            onClick={() => props.onClose()}
          >
            <Icon name="x" />
          </button>
        </div>
        <ol class="wzbar">
          <For each={STEPS}>
            {(s, i) => (
              <li class={i() < step() ? "done" : i() === step() ? "cur" : ""}>
                <button
                  type="button"
                  disabled={i() > step()}
                  onClick={() => setStep(i())}
                >
                  {i() < step() ? "✓ " : ""}
                  {t(`events.wizard.step.${s}`)}
                </button>
              </li>
            )}
          </For>
        </ol>
        <div class="dlg-b stack" style={{ "padding-top": "14px" }}>
          <Show when={STEPS[step()] === "subject"}>
            <div style={{ display: "grid", gap: "8px" }}>
              <For each={TYPES}>
                {(x) => (
                  <label
                    class="opt"
                    style={x.ready ? undefined : { opacity: 0.5 }}
                  >
                    <input
                      type="radio"
                      name="ruleType"
                      checked={type() === x.key}
                      disabled={!x.ready}
                      onChange={() => setType(x.key)}
                    />
                    <div>
                      <b>
                        <Icon name={x.icon} />{" "}
                        {t(`events.wizard.type.${x.key}`)}
                        <Show when={!x.ready}>
                          {" "}
                          <span class="chip">{t("events.wizard.soon")}</span>
                        </Show>
                      </b>
                      <small>{t(`events.wizard.typeHint.${x.key}`)}</small>
                    </div>
                  </label>
                )}
              </For>
            </div>
            <Show when={type() === "nodata"}>
              <div class="seg" role="group">
                <button
                  type="button"
                  aria-pressed={!scopeAll()}
                  onClick={() => setScopeAll(false)}
                >
                  {t("events.wizard.onePoint")}
                </button>
                <button
                  type="button"
                  aria-pressed={scopeAll()}
                  onClick={() => setScopeAll(true)}
                >
                  {t("events.wizard.allInPlace")}
                </button>
              </div>
            </Show>
            <Show
              when={type() === "nodata" && scopeAll()}
              fallback={
                <div class="field">
                  <label for="wzPoint">{t("assets.point")}</label>
                  <select
                    id="wzPoint"
                    value={form().pointId ?? ""}
                    onChange={(e) => set({ pointId: e.currentTarget.value })}
                  >
                    <option value="" disabled>
                      {t("events.wizard.pickPoint")}
                    </option>
                    <For each={points()}>
                      {(p) => (
                        <option
                          value={p.id}
                        >{`${p.name} · ${nodeName(p.nodeId)}`}</option>
                      )}
                    </For>
                  </select>
                  <Show
                    when={type() === "thr" && (quantities() ?? []).length > 1}
                  >
                    <label for="wzParam" style={{ "margin-top": "8px" }}>
                      {t("events.wizard.parameter")}
                    </label>
                    <select
                      id="wzParam"
                      value={form().parameter ?? ""}
                      onChange={(e) =>
                        set({ parameter: e.currentTarget.value || null })
                      }
                    >
                      <option value="">{t("events.wizard.pointValue")}</option>
                      <For each={quantities()}>
                        {(q) => (
                          <option value={q}>
                            {t("events.wizard.everyPhase", {
                              name: tOr(`attribute.${q}`, q),
                            })}
                          </option>
                        )}
                      </For>
                    </select>
                    <Show when={(quantities() ?? []).includes("voltage")}>
                      <button
                        type="button"
                        class="btn sm"
                        style={{ "margin-top": "6px", "justify-self": "start" }}
                        onClick={voltageBand}
                      >
                        {t("events.wizard.voltagePreset")}
                      </button>
                    </Show>
                    <span class="hint">{t("events.wizard.parameterHint")}</span>
                  </Show>
                </div>
              }
            >
              <div class="g-2e grid" style={{ gap: "10px" }}>
                <div class="field">
                  <label for="wzNode">{t("events.wizard.place")}</label>
                  <select
                    id="wzNode"
                    value={form().nodeId ?? ""}
                    onChange={(e) => set({ nodeId: e.currentTarget.value })}
                  >
                    <option value="" disabled>
                      {t("events.wizard.pickPlace")}
                    </option>
                    <For each={nodes()}>
                      {({ node, depth }) => (
                        <option
                          value={node.id}
                        >{`${"  ".repeat(depth)}${node.name}`}</option>
                      )}
                    </For>
                  </select>
                </div>
                <div class="field">
                  <label for="wzMedium">{t("assets.medium")}</label>
                  <select
                    id="wzMedium"
                    value={form().medium ?? ""}
                    onChange={(e) =>
                      set({ medium: e.currentTarget.value || null })
                    }
                  >
                    <option value="">{t("events.rule.anyMedium")}</option>
                    <For each={MEDIA_IDS}>
                      {(m) => <option value={m}>{t(`medium.${m}`)}</option>}
                    </For>
                  </select>
                </div>
              </div>
            </Show>
            <div class="field">
              <label for="wzName">{t("events.rules.name")}</label>
              <input
                id="wzName"
                maxLength={120}
                value={form().name}
                placeholder={t("events.wizard.namePlaceholder")}
                onInput={(e) => set({ name: e.currentTarget.value })}
              />
            </div>
          </Show>

          <Show when={STEPS[step()] === "condition"}>
            <Show
              when={type() === "thr"}
              fallback={
                <>
                  <div class="field" style={{ "max-width": "320px" }}>
                    <label for="wzLimit">
                      {t("events.wizard.noDataLonger")}
                    </label>
                    <select
                      id="wzLimit"
                      value={String(form().minutes)}
                      onChange={(e) =>
                        set({ minutes: Number(e.currentTarget.value) })
                      }
                    >
                      <For each={NO_DATA}>
                        {(m) => (
                          <option value={m}>
                            {t("events.wizard.minutes", { minutes: m })}
                          </option>
                        )}
                      </For>
                    </select>
                  </div>
                  <WindowField
                    value={form().window}
                    onChange={(window) => set({ window })}
                  />
                  <p class="hint" style={{ margin: 0 }}>
                    {t("events.wizard.noDataHint")}
                  </p>
                </>
              }
            >
              <div
                class="grid"
                style={{
                  "grid-template-columns": "repeat(4, minmax(0, 1fr))",
                  gap: "10px",
                }}
              >
                <div class="field">
                  <label for="wzOp">{t("events.wizard.when")}</label>
                  <select
                    id="wzOp"
                    value={form().kind}
                    onChange={(e) =>
                      set({ kind: e.currentTarget.value as RuleInput["kind"] })
                    }
                  >
                    <For each={["above", "below", "outside"] as const}>
                      {(k) => (
                        <option value={k}>{t(`events.wizard.op.${k}`)}</option>
                      )}
                    </For>
                  </select>
                </div>
                <div class="field">
                  <label for="wzVal">
                    {form().kind === "outside"
                      ? t("events.wizard.from")
                      : t("events.wizard.threshold")}{" "}
                    ({unit()})
                  </label>
                  <input
                    id="wzVal"
                    inputmode="decimal"
                    value={form().threshold ?? ""}
                    onChange={(e) =>
                      set({ threshold: num(e.currentTarget.value) })
                    }
                  />
                </div>
                <Show when={form().kind === "outside"}>
                  <div class="field">
                    <label for="wzVal2">
                      {t("events.wizard.to")} ({unit()})
                    </label>
                    <input
                      id="wzVal2"
                      inputmode="decimal"
                      value={form().threshold2 ?? ""}
                      onChange={(e) =>
                        set({ threshold2: num(e.currentTarget.value) })
                      }
                    />
                  </div>
                </Show>
                <div class="field">
                  <label for="wzDur">{t("events.wizard.atLeast")}</label>
                  <select
                    id="wzDur"
                    value={String(form().minutes)}
                    onChange={(e) =>
                      set({ minutes: Number(e.currentTarget.value) })
                    }
                  >
                    <For each={DURATIONS}>
                      {(m) => (
                        <option value={m}>
                          {m === 0
                            ? t("events.wizard.immediately")
                            : t("events.wizard.minutes", { minutes: m })}
                        </option>
                      )}
                    </For>
                  </select>
                </div>
                <div class="field">
                  <label for="wzHyst">
                    {t("events.wizard.hysteresis")} ({unit()})
                  </label>
                  <input
                    id="wzHyst"
                    inputmode="decimal"
                    value={form().hysteresis ?? ""}
                    onChange={(e) =>
                      set({ hysteresis: num(e.currentTarget.value) })
                    }
                  />
                </div>
              </div>
              <div class="g-2e grid" style={{ gap: "10px" }}>
                <WindowField
                  value={form().window}
                  onChange={(window) => set({ window })}
                />
                <div class="field">
                  <label for="wzNoData">{t("events.wizard.onNoData")}</label>
                  <select id="wzNoData" disabled>
                    <option>{t("events.wizard.noDataSkip")}</option>
                  </select>
                </div>
              </div>
              <div>
                <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
                  {t("events.wizard.preview")}
                </div>
                <Show
                  when={!preview.error}
                  fallback={<ErrorBox error={preview.error} />}
                >
                  <Show
                    when={preview()}
                    fallback={
                      <p class="hint" style={{ margin: 0 }}>
                        {t("events.wizard.previewNeedsThreshold")}
                      </p>
                    }
                  >
                    {(p) => (
                      <>
                        <Chart
                          series={[
                            {
                              key: "v",
                              label: pointName(form().pointId ?? ""),
                              color: "var(--m-el)",
                              kind: "line",
                              points: p().values.map((v) => ({
                                t: new Date(v.at).getTime(),
                                v: v.value,
                              })),
                            },
                          ]}
                          from={new Date(Date.now() - 7 * 86_400_000)}
                          to={new Date()}
                          unit={unit()}
                          locale={locale()}
                          bucketMs={900_000}
                          height={160}
                          emptyText={t("events.wizard.noPreview")}
                          thresholds={[
                            {
                              value: form().threshold!,
                              label: t("events.wizard.thresholdLabel", {
                                value: formatNumber(
                                  form().threshold,
                                  locale(),
                                  2,
                                ),
                              }),
                            },
                            ...(form().kind === "outside" &&
                            form().threshold2 !== null
                              ? [
                                  {
                                    value: form().threshold2!,
                                    label: t("events.wizard.thresholdLabel", {
                                      value: formatNumber(
                                        form().threshold2,
                                        locale(),
                                        2,
                                      ),
                                    }),
                                  },
                                ]
                              : []),
                          ]}
                          bands={p().runs.map((r) => ({
                            from: new Date(r.from).getTime(),
                            to: new Date(r.to).getTime(),
                          }))}
                        />
                        <p class="hint" style={{ margin: "4px 0 0" }}>
                          <b
                            style={{
                              color: p().runs.length
                                ? "var(--crit)"
                                : "var(--ok)",
                              "font-weight": 600,
                            }}
                          >
                            {p().runs.length
                              ? t("events.wizard.wouldFire", {
                                  count: p().runs.length,
                                })
                              : t("events.wizard.wouldNotFire")}
                          </b>
                          {p().runs.length
                            ? ` · ${t("events.wizard.hoursBeyond", { hours: formatNumber(hours(), locale(), 1) })}`
                            : ""}
                        </p>
                      </>
                    )}
                  </Show>
                </Show>
              </div>
              <p class="hint" style={{ margin: 0 }}>
                {t("events.wizard.edgeHint")}
              </p>
            </Show>
          </Show>

          <Show when={STEPS[step()] === "recipients"}>
            <div class="field">
              <span class="eyebrow">{t("events.rules.priority")}</span>
              <div class="seg" role="group">
                <For each={[1, 2, 3]}>
                  {(p) => (
                    <button
                      type="button"
                      aria-pressed={form().priority === p}
                      onClick={() => set({ priority: p })}
                    >
                      {t(`events.prio.${p}`)}
                    </button>
                  )}
                </For>
              </div>
            </div>
            <div class="g-2e grid" style={{ gap: "14px" }}>
              <div>
                <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
                  {t("events.wizard.recipients")}
                </div>
                <For
                  each={users() ?? []}
                  fallback={<p class="hint">{t("events.wizard.noUsers")}</p>}
                >
                  {(u) => (
                    <label
                      class="check"
                      style={{
                        "grid-template-columns": "22px 1fr",
                        padding: "5px 0",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form().recipients.includes(u.id)}
                        onChange={(e) =>
                          set({
                            recipients: e.currentTarget.checked
                              ? [...form().recipients, u.id]
                              : form().recipients.filter((x) => x !== u.id),
                          })
                        }
                      />
                      <span>
                        {u.name}
                        <Show when={u.email}>
                          <span class="hint"> · {u.email}</span>
                        </Show>
                      </span>
                    </label>
                  )}
                </For>
              </div>
              <div>
                <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
                  {t("events.wizard.channels")}
                </div>
                <For each={CHANNELS}>
                  {(c) => (
                    <label
                      class="check"
                      style={{
                        "grid-template-columns": "22px 1fr",
                        padding: "5px 0",
                        opacity: c === "app" ? 1 : 0.5,
                      }}
                    >
                      <input type="checkbox" checked={c === "app"} disabled />
                      <span>
                        {t(`events.channel.${c}`)}
                        <Show when={c !== "app"}>
                          {" "}
                          <span class="chip">{t("events.wizard.soon")}</span>
                        </Show>
                      </span>
                    </label>
                  )}
                </For>
              </div>
            </div>
            <p class="hint" style={{ margin: 0 }}>
              {t("events.wizard.recipientsHint")}
            </p>
          </Show>

          <Show when={STEPS[step()] === "escalation"}>
            <div
              class="grid"
              style={{
                "grid-template-columns": "repeat(3, minmax(0, 1fr))",
                gap: "10px",
              }}
            >
              <div class="field">
                <label for="wzEsc">{t("events.wizard.escalateAfter")}</label>
                <select
                  id="wzEsc"
                  value={String(form().escalateAfter ?? 0)}
                  onChange={(e) =>
                    set({
                      escalateAfter: Number(e.currentTarget.value) || null,
                    })
                  }
                >
                  <For each={ESCALATIONS}>
                    {(m) => (
                      <option value={m}>
                        {m === 0
                          ? t("events.wizard.noEscalation")
                          : t("events.wizard.minutes", { minutes: m })}
                      </option>
                    )}
                  </For>
                </select>
              </div>
              <div class="field">
                <label for="wzEscTo">{t("events.wizard.notify")}</label>
                <select
                  id="wzEscTo"
                  value={form().escalateTo ?? ""}
                  disabled={!form().escalateAfter}
                  onChange={(e) =>
                    set({ escalateTo: e.currentTarget.value || null })
                  }
                >
                  <option value="">—</option>
                  <For each={users() ?? []}>
                    {(u) => <option value={u.id}>{u.name}</option>}
                  </For>
                </select>
              </div>
              <div class="field">
                <label for="wzEscCh">{t("events.wizard.channel")}</label>
                <select id="wzEscCh" disabled>
                  <option>{t("events.channel.app")}</option>
                </select>
              </div>
            </div>
            <div class="g-2e grid" style={{ gap: "10px" }}>
              <div class="field">
                <label for="wzAuto">{t("events.wizard.onClear")}</label>
                <select
                  id="wzAuto"
                  value={form().autoClose ? "close" : "keep"}
                  onChange={(e) =>
                    set({ autoClose: e.currentTarget.value === "close" })
                  }
                >
                  <option value="keep">{t("events.wizard.keepOpen")}</option>
                  <option value="close">{t("events.wizard.autoClose")}</option>
                </select>
              </div>
              <div class="field">
                <label for="wzRem">{t("events.wizard.reminder")}</label>
                <select
                  id="wzRem"
                  value={String(form().remindHours ?? 0)}
                  onChange={(e) =>
                    set({ remindHours: Number(e.currentTarget.value) || null })
                  }
                >
                  <option value="0">{t("events.wizard.noReminder")}</option>
                  <option value="4">{t("events.wizard.every4h")}</option>
                  <option value="24">{t("events.wizard.daily")}</option>
                </select>
              </div>
            </div>
            <p class="hint" style={{ margin: 0 }}>
              {t("events.wizard.escalationHint")}
            </p>
          </Show>

          <Show when={STEPS[step()] === "summary"}>
            <table class="tbl sumtbl">
              <tbody>
                <tr>
                  <td>{t("events.wizard.sum.rule")}</td>
                  <td>
                    <b style={{ "font-weight": 500 }}>{form().name}</b> ·{" "}
                    {t(`events.wizard.type.${type()}`)}
                  </td>
                </tr>
                <tr>
                  <td>{t("events.wizard.sum.subject")}</td>
                  <td>
                    {subjectText(
                      scopeAll() ? { ...form(), pointId: null } : form(),
                      pointName,
                      nodeName,
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{t("events.rules.condition")}</td>
                  <td>{conditionText(form(), unit())}</td>
                </tr>
                <tr>
                  <td>{t("events.wizard.sum.who")}</td>
                  <td>
                    {t(`events.prio.${form().priority}`)} ·{" "}
                    {form().recipients.length > 0
                      ? form()
                          .recipients.map((id) => userName(users(), id))
                          .join(", ")
                      : t("events.wizard.nobody")}{" "}
                    · {t("events.channel.app")}
                  </td>
                </tr>
                <tr>
                  <td>{t("events.wizard.sum.escalation")}</td>
                  <td>
                    {form().escalateAfter
                      ? t("events.wizard.escalationText", {
                          minutes: form().escalateAfter!,
                          who: userName(users(), form().escalateTo),
                        })
                      : t("events.wizard.noEscalation")}
                  </td>
                </tr>
              </tbody>
            </table>
            <label class="opt">
              <input
                type="checkbox"
                checked={form().test}
                onChange={(e) => set({ test: e.currentTarget.checked })}
              />
              <div>
                <b>{t("events.wizard.testMode")}</b>
                <small>{t("events.wizard.testModeHint")}</small>
              </div>
            </label>
          </Show>

          <Show when={problem()}>
            <div
              class="note"
              style={{
                margin: 0,
                border: 0,
                "border-radius": "6px",
                color: "var(--warn)",
              }}
            >
              <Icon name="alert" />
              {problem()}
            </div>
          </Show>
          <Show when={error()}>
            <ErrorBox error={error()} />
          </Show>
        </div>
        <div class="dlg-f">
          <Show
            when={step() > 0}
            fallback={
              <button type="button" class="btn" onClick={() => props.onClose()}>
                {t("common.cancel")}
              </button>
            }
          >
            <button
              type="button"
              class="btn"
              onClick={() => setStep(step() - 1)}
            >
              <Icon name="chevl" />
              {t("common.back")}
            </button>
          </Show>
          <button
            type="button"
            class="btn primary"
            disabled={busy()}
            onClick={next}
          >
            {step() === STEPS.length - 1
              ? t("events.wizard.save")
              : t("common.next")}
            <Show when={step() < STEPS.length - 1}>
              <Icon name="chev" />
            </Show>
          </button>
        </div>
      </div>
    </div>
  );
}

function WindowField(props: {
  value: RuleInput["window"];
  onChange: (w: RuleInput["window"]) => void;
}) {
  return (
    <div class="field">
      <label for="wzWin">{t("events.wizard.whenCheck")}</label>
      <select
        id="wzWin"
        value={props.value}
        onChange={(e) =>
          props.onChange(e.currentTarget.value as RuleInput["window"])
        }
      >
        <For each={["always", "work", "off", "weekend"] as const}>
          {(w) => <option value={w}>{t(`events.window.${w}`)}</option>}
        </For>
      </select>
    </div>
  );
}
